/**
 * The one wording of a bought line's despatch and arrival, part by part
 * (orders' #2748; supplier interface spec 05 §4), for the order page and the
 * purchase order page. The views send the facts (`despatch_progress`); these
 * sentences are the only place they are worded, so a change of words is a
 * change here (Ben, 10 Oct):
 *
 * - Awaiting despatch: nothing sent.
 * - Part sent: "3,000 of 5,000 sent · last 12 Oct".
 * - Sent: "5,000 sent · 12 Oct", or "5,000 sent in 2 deliveries · last 14 Oct".
 * - Part delivered: "3,000 of 5,000 delivered" ("received" into stock).
 * - Delivered / Received: once every part has arrived.
 *
 * Part sent and Sent carry the staff status the rest of delivery uses,
 * "Part despatched" and "Despatched" (order to invoice doc 102 §12), ahead of
 * the sentence: `despatchProgressStatus`.
 */
import type {
  LineDespatchProgress,
  LineDespatchRow,
} from "../data/order-to-invoice-types";
import { formatShortDateOrDash } from "./formatters";

const count = (n: number) => n.toLocaleString("en-GB");

/** The line's status sentence. */
export function despatchProgressSentence(
  progress: LineDespatchProgress,
): string {
  const quantity = count(progress.quantity);
  const last = progress.last_despatched_on
    ? formatShortDateOrDash(progress.last_despatched_on)
    : null;
  switch (progress.state) {
    case "awaiting_despatch":
      return "Awaiting despatch";
    case "part_sent":
      return `${count(progress.despatched_quantity)} of ${quantity} sent${last ? ` · last ${last}` : ""}`;
    case "sent":
      if (progress.delivery_count > 1)
        return `${quantity} sent in ${progress.delivery_count} deliveries${last ? ` · last ${last}` : ""}`;
      return `${quantity} sent${last ? ` · ${last}` : ""}`;
    case "part_delivered":
      return `${count(progress.arrived_quantity)} of ${quantity} ${progress.is_into_stock ? "received" : "delivered"}`;
    case "delivered":
      return "Delivered";
    case "received":
      return "Received";
  }
}

/** The staff status of a line sent in parts, or null where the sentence says it all. */
export function despatchProgressLabel(
  progress: LineDespatchProgress,
): string | null {
  switch (progress.state) {
    case "part_sent":
      return "Part despatched";
    case "sent":
      return "Despatched";
    default:
      return null;
  }
}

/** The row's status: "Part despatched · 3,000 of 5,000 sent · last 12 Oct". */
export function despatchProgressStatus(progress: LineDespatchProgress): string {
  return [despatchProgressLabel(progress), despatchProgressSentence(progress)]
    .filter(Boolean)
    .join(" · ");
}

/** A delivery's carrier as the row shows it: its name, a free-text carrier's own name, or that none was recorded. */
export function deliveryCarrier(row: LineDespatchRow): string {
  return row.carrier_label ?? "Carrier not recorded";
}

/** A delivery row's lead: "3,000 sent 12 Oct". */
export function deliverySent(row: LineDespatchRow): string {
  return `${count(row.quantity)} sent ${formatShortDateOrDash(row.despatched_on)}`;
}

/** The step a delivery's arrival is recorded with, in its button's words. */
export function deliveryRecordLabel(row: LineDespatchRow): string | null {
  if (!row.record) return null;
  return row.record.kind === "record_delivered"
    ? "Record delivered"
    : "Record received";
}
